
import React, { useCallback, useEffect, useState } from "react";
import {
  Plus,
  Pencil,
  Trash2,
  Image as ImageIcon,
  RefreshCw,
  Search,
  Eye,
  X,
  Upload,
  Check,
  AlertCircle,
  Layers3,
  EyeOff,
  ArrowUpDown,
  CalendarDays,
  ExternalLink,
  Monitor,
  ImagePlus,
  ChevronDown,
} from "lucide-react";

import Modal from "../components/Modal";
import { PageHeader, StatCard } from "../components/ui";

import {
  EmptyState,
  ErrorMessage,
  Loading,
  SuccessMessage,
} from "../components/Feedback";

import {
  api,
  endpoint,
  getErrorMessage,
  unwrapList,
} from "../services/api";

/* =========================================================
   CONSTANTS
========================================================= */

const createBlankForm = () => ({
  title: "",
  isActive: true,
  order: 0,
});

const inputClass = "input";

const labelClass = "label";

/* =========================================================
   HELPERS
========================================================= */

function imageUrl(value) {
  if (!value) return "";

  if (/^https?:\/\//i.test(value)) {
    // Use this conversion only for local development.
    return value.replace(
      /^https:\/\/localhost:4480(?=\/)/i,
      "https://pmswebbackend.pixelmindsolutions.com"
    );
  }

  return endpoint(value.startsWith("/") ? value : `/${value}`);
}

function formatDate(value) {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

/* =========================================================
   REUSABLE UI COMPONENTS
========================================================= */

function StatusBadge({ active }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${
        active
          ? "bg-emerald-50 text-emerald-700"
          : "bg-slate-100 text-slate-600"
      }`}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${
          active ? "bg-emerald-500" : "bg-slate-400"
        }`}
      />

      {active ? "Active" : "Inactive"}
    </span>
  );
}

function SectionHeading({
  icon: Icon,
  title,
  description,
}) {
  return (
    <div className="mb-5 flex items-start gap-3">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-700">
        <Icon size={19} />
      </div>

      <div>
        <h3 className="font-semibold text-slate-900">
          {title}
        </h3>

        {description && (
          <p className="mt-1 text-sm leading-5 text-slate-500">
            {description}
          </p>
        )}
      </div>
    </div>
  );
}

/* =========================================================
   BANNER IMAGE UPLOAD WITH PREVIEW
========================================================= */

function BannerImageUpload({
  file,
  existingImage,
  onChange,
  required = false,
}) {
  const [preview, setPreview] = useState("");

  useEffect(() => {
    if (!file) {
      setPreview("");
      return undefined;
    }

    const objectUrl = URL.createObjectURL(file);

    setPreview(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  const currentPreview = preview || imageUrl(existingImage);

  return (
    <div>
      <label className={labelClass}>
        Banner image
        {required && (
          <span className="ml-1 text-red-500">*</span>
        )}
      </label>

      <p className="mb-3 text-xs leading-5 text-slate-500">
        Recommended: a high-quality landscape image with a
        16:8 or 16:9 aspect ratio.
      </p>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
        <div className="relative flex aspect-[16/8] items-center justify-center overflow-hidden">
          {currentPreview ? (
            <img
              src={currentPreview}
              alt="Banner preview"
              className="h-full w-full object-cover"
              onError={(event) => {
                event.currentTarget.style.opacity = "0";
              }}
            />
          ) : (
            <div className="flex flex-col items-center gap-3 p-6 text-center text-slate-500">
              <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-white shadow-sm">
                <ImagePlus size={26} />
              </div>

              <div>
                <p className="text-sm font-semibold text-slate-600">
                  No image selected
                </p>

                <p className="mt-1 text-xs">
                  Choose an image to see the preview.
                </p>
              </div>
            </div>
          )}

          {currentPreview && (
            <div className="absolute bottom-3 left-3 rounded-lg bg-black/60 px-3 py-1.5 text-xs font-medium text-white backdrop-blur-sm">
              Banner preview
            </div>
          )}
        </div>

        <div className="border-t border-slate-200 bg-white p-3">
          <label className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:border-brand-300 hover:bg-brand-50 hover:text-brand-800">
            <Upload size={16} />
            {file ? "Choose another image" : "Upload banner image"}

            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              className="sr-only"
              required={required && !existingImage && !file}
              onChange={(event) => {
                onChange(event.target.files?.[0] || null);
                event.target.value = "";
              }}
            />
          </label>

          {file && (
            <div className="mt-3 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="break-all text-xs font-medium text-slate-700">
                  {file.name}
                </p>

                <p className="mt-1 text-xs text-slate-500">
                  {(file.size / (1024 * 1024)).toFixed(2)} MB
                </p>
              </div>

              <button
                type="button"
                onClick={() => onChange(null)}
                className="shrink-0 rounded-lg p-2 text-slate-500 transition hover:bg-red-50 hover:text-red-600"
                title="Remove selected image"
              >
                <X size={16} />
              </button>
            </div>
          )}

          {!file && existingImage && (
            <p className="mt-2 break-all text-xs text-slate-500">
              Current image will be retained unless replaced.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

/* =========================================================
   BANNER CARD
========================================================= */

function BannerCard({
  item,
  onView,
  onEdit,
  onDelete,
}) {
  return (
    <article className="group overflow-hidden rounded-xl border border-slate-200 bg-white transition duration-200 hover:-translate-y-0.5 hover:border-brand-200 hover:shadow-lg hover:shadow-slate-200/50">
      {/* Image */}
      <div className="relative aspect-[16/8] overflow-hidden bg-slate-100">
        {item.image ? (
          <img
            src={imageUrl(item.image)}
            alt={item.title || "Website banner"}
            loading="lazy"
            className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]"
            onError={(event) => {
              event.currentTarget.style.display = "none";
            }}
          />
        ) : (
          <div className="flex h-full items-center justify-center text-slate-300">
            <ImageIcon size={42} />
          </div>
        )}

        <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-black/10" />

        <div className="absolute left-3 top-3">
          <StatusBadge active={item.isActive !== false} />
        </div>

        <div className="absolute right-3 top-3 inline-flex items-center gap-1.5 rounded-lg border border-white/40 bg-white/90 px-2.5 py-1.5 text-xs font-semibold text-slate-700 shadow-sm backdrop-blur-sm">
          <ArrowUpDown size={13} />
          Order {item.order ?? 0}
        </div>

        <button
          type="button"
          onClick={() => onView(item)}
          className="absolute bottom-3 right-3 inline-flex items-center gap-2 rounded-lg bg-white/95 px-3 py-2 text-xs font-semibold text-slate-800 opacity-100 shadow-sm transition hover:bg-white sm:opacity-0 sm:group-hover:opacity-100"
        >
          <Eye size={15} />
          Preview
        </button>
      </div>

      {/* Details */}
      <div className="p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="break-words font-semibold text-slate-900">
              {item.title || "Untitled banner"}
            </h3>

            <p className="mt-1 text-xs text-slate-500">
              {item.createdAt
                ? `Created ${formatDate(item.createdAt)}`
                : "Website banner"}
            </p>
          </div>

          <div className="flex shrink-0 items-center gap-1.5">
            <button
              type="button"
              onClick={() => onView(item)}
              title="View banner"
              aria-label={`View ${item.title || "banner"}`}
              className="rounded-lg border border-slate-200 p-2 text-slate-600 transition hover:border-accent-100 hover:bg-accent-50 hover:text-accent-700"
            >
              <Eye size={16} />
            </button>

            <button
              type="button"
              onClick={() => onEdit(item)}
              title="Edit banner"
              aria-label={`Edit ${item.title || "banner"}`}
              className="rounded-lg border border-slate-200 p-2 text-slate-600 transition hover:border-brand-200 hover:bg-brand-50 hover:text-brand-800"
            >
              <Pencil size={16} />
            </button>

            <button
              type="button"
              onClick={() => onDelete(item)}
              title="Delete banner"
              aria-label={`Delete ${item.title || "banner"}`}
              className="rounded-lg border border-slate-200 p-2 text-slate-600 transition hover:border-red-200 hover:bg-red-50 hover:text-red-600"
            >
              <Trash2 size={16} />
            </button>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between gap-3 border-t border-slate-100 pt-3">
          <div className="flex min-w-0 items-center gap-2 text-xs text-slate-500">
            <ImageIcon size={14} className="shrink-0" />

            <span className="truncate">
              {item.image
                ? item.image.split("/").pop()
                : "No image"}
            </span>
          </div>

          {item.isActive !== false ? (
            <span className="shrink-0 text-xs font-semibold text-emerald-600">
              Published
            </span>
          ) : (
            <span className="shrink-0 text-xs font-semibold text-slate-500">
              Hidden
            </span>
          )}
        </div>
      </div>
    </article>
  );
}

/* =========================================================
   MAIN BANNERS PAGE
========================================================= */

export default function Banners() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("order");

  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(createBlankForm);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [viewModal, setViewModal] = useState(false);
  const [viewBanner, setViewBanner] = useState(null);

  /* ---------------------------------------------------------
     LOAD BANNERS
  --------------------------------------------------------- */

  const load = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await api.get("/api/banners");

      setItems(unwrapList(response));
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /* ---------------------------------------------------------
     OPEN CREATE / EDIT MODAL
  --------------------------------------------------------- */

  const open = (item = null) => {
    setEditing(item);

    setForm(
      item
        ? {
            title: item.title || "",
            isActive: item.isActive !== false,
            order: item.order ?? 0,
          }
        : createBlankForm()
    );

    setFile(null);
    setError("");
    setSuccess("");
    setModal(true);
  };

  const closeForm = () => {
    if (saving) return;

    setModal(false);
    setEditing(null);
    setForm(createBlankForm());
    setFile(null);
  };

  /* ---------------------------------------------------------
     CREATE / UPDATE BANNER
  --------------------------------------------------------- */

  const save = async (event) => {
    event.preventDefault();

    setError("");
    setSuccess("");

    if (!form.title.trim()) {
      setError("Banner title is required.");
      return;
    }

    if (!editing && !file) {
      setError("Please select a banner image.");
      return;
    }

    if (
      file &&
      !file.type.startsWith("image/")
    ) {
      setError("Please upload a valid image file.");
      return;
    }

    setSaving(true);

    try {
      const formData = new FormData();

      formData.append("title", form.title.trim());
      formData.append("isActive", String(form.isActive));
      formData.append("order", String(form.order));

      if (file) {
        formData.append("image", file);
      }

      if (editing) {
        await api.put(
          `/api/banners/${editing._id}`,
          formData
        );
      } else {
        await api.post("/api/banners", formData);
      }

      setModal(false);
      setEditing(null);
      setForm(createBlankForm());
      setFile(null);

      setSuccess(
        editing
          ? "Banner updated successfully."
          : "Banner created successfully."
      );

      await load();
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setSaving(false);
    }
  };

  /* ---------------------------------------------------------
     DELETE BANNER
  --------------------------------------------------------- */

  const remove = async () => {
    if (!deleteTarget || deleting) return;

    setDeleting(true);

    try {
      await api.delete(
        `/api/banners/${deleteTarget._id}`
      );

      setItems((previous) =>
        previous.filter(
          (item) => item._id !== deleteTarget._id
        )
      );

      setSuccess("Banner deleted successfully.");
      setDeleteTarget(null);
    } catch (requestError) {
      setError(getErrorMessage(requestError));
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  /* ---------------------------------------------------------
     VIEW BANNER
  --------------------------------------------------------- */

  const openView = (item) => {
    setViewBanner(item);
    setViewModal(true);
  };

  /* ---------------------------------------------------------
     FILTER / SORT
  --------------------------------------------------------- */

  const filteredItems = items
    .filter((item) => {
      const searchText = [
        item.title,
        item.image,
        item.order,
      ]
        .join(" ")
        .toLowerCase();

      const matchesSearch = searchText.includes(
        query.toLowerCase().trim()
      );

      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "active" &&
          item.isActive !== false) ||
        (statusFilter === "inactive" &&
          item.isActive === false);

      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      if (sortBy === "title") {
        return (a.title || "").localeCompare(
          b.title || ""
        );
      }

      if (sortBy === "newest") {
        return (
          new Date(b.createdAt || 0).getTime() -
          new Date(a.createdAt || 0).getTime()
        );
      }

      return Number(a.order ?? 0) - Number(b.order ?? 0);
    });

  const activeCount = items.filter(
    (item) => item.isActive !== false
  ).length;

  const inactiveCount = items.length - activeCount;

  /* =========================================================
     RENDER
  ========================================================= */

  return (
    <div className="space-y-6 pb-8">
      {/* PAGE HEADER */}
      <PageHeader
        title="Website banners"
        description="Manage website hero images, promotional banners, visibility, and display order from one place."
      >
        <button type="button" onClick={() => open()} className="btn-primary">
          <Plus size={17} />
          Add banner
        </button>
      </PageHeader>

      {/* FEEDBACK */}
      <SuccessMessage>{success}</SuccessMessage>
      <ErrorMessage>{error}</ErrorMessage>

      {/* STATS */}
      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        <StatCard
          title="Total banners"
          value={items.length}
          description="All uploaded banners"
          icon={Layers3}
          iconClass="bg-brand-50 text-brand-700"
        />

        <StatCard
          title="Active banners"
          value={activeCount}
          description="Available for display"
          icon={Eye}
          iconClass="bg-emerald-50 text-emerald-600"
        />

        <StatCard
          title="Inactive banners"
          value={inactiveCount}
          description="Currently hidden"
          icon={EyeOff}
          iconClass="bg-slate-100 text-slate-600"
        />

        <StatCard
          title="Displayed first"
          value={
            items.length
              ? Math.min(
                  ...items.map((item) =>
                    Number(item.order ?? 0)
                  )
                )
              : "—"
          }
          description="Lowest display order"
          icon={ArrowUpDown}
          iconClass="bg-accent-50 text-accent-700"
        />
      </div>

      {/* MANAGEMENT PANEL */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        {/* Toolbar */}
        <div className="border-b border-slate-100 p-4 sm:p-5">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
            <div>
              <h2 className="font-semibold text-slate-900">
                All banners
              </h2>

              <p className="mt-1 text-sm text-slate-500">
                Browse, preview, and manage your website images.
              </p>
            </div>

            <button
              type="button"
              onClick={load}
              disabled={loading}
              className="btn-secondary self-start"
            >
              <RefreshCw
                size={15}
                className={loading ? "animate-spin" : ""}
              />
              Refresh
            </button>
          </div>

          <div className="mt-4 grid gap-3 md:grid-cols-[minmax(0,1fr)_190px_190px]">
            {/* Search */}
            <div className="relative">
              <Search
                size={17}
                className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500"
              />

              <input
                className={`${inputClass} pl-10`}
                value={query}
                onChange={(event) =>
                  setQuery(event.target.value)
                }
                placeholder="Search banners..."
              />
            </div>

            {/* Status filter */}
            <div className="relative">
              <select
                className={`${inputClass} appearance-none pr-10`}
                value={statusFilter}
                onChange={(event) =>
                  setStatusFilter(event.target.value)
                }
              >
                <option value="all">All statuses</option>
                <option value="active">Active only</option>
                <option value="inactive">Inactive only</option>
              </select>

              <ChevronDown
                size={16}
                className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-500"
              />
            </div>

            {/* Sorting */}
            <div className="relative">
              <select
                className={`${inputClass} appearance-none pr-10`}
                value={sortBy}
                onChange={(event) =>
                  setSortBy(event.target.value)
                }
              >
                <option value="order">Sort by display order</option>
                <option value="title">Sort by title</option>
                <option value="newest">Newest first</option>
              </select>

              <ChevronDown
                size={16}
                className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-500"
              />
            </div>
          </div>

          {/* Result count */}
          <div className="mt-4 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
            <span>
              Showing{" "}
              <strong className="text-slate-700">
                {filteredItems.length}
              </strong>{" "}
              of{" "}
              <strong className="text-slate-700">
                {items.length}
              </strong>{" "}
              banners
            </span>

            {(query || statusFilter !== "all") && (
              <button
                type="button"
                onClick={() => {
                  setQuery("");
                  setStatusFilter("all");
                }}
                className="inline-flex items-center gap-1.5 font-semibold text-brand-700 hover:text-brand-800"
              >
                <X size={13} />
                Clear filters
              </button>
            )}
          </div>
        </div>

        {/* Banner list */}
        <div className="p-4 sm:p-5">
          {loading ? (
            <div className="py-8">
              <Loading label="Loading website banners..." />
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="rounded-lg border border-dashed border-slate-200 p-5 sm:p-8">
              <EmptyState
                title={
                  query || statusFilter !== "all"
                    ? "No matching banners"
                    : "No banners yet"
                }
                detail={
                  query || statusFilter !== "all"
                    ? "Try changing your search or filters."
                    : "Upload your first website banner to get started."
                }
              />

              {!query && statusFilter === "all" && (
                <div className="mt-4 flex justify-center">
                  <button
                    type="button"
                    onClick={() => open()}
                    className="btn-primary"
                  >
                    <Plus size={16} />
                    Add your first banner
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="grid gap-5 md:grid-cols-2 2xl:grid-cols-3">
              {filteredItems.map((item) => (
                <BannerCard
                  key={item._id}
                  item={item}
                  onView={openView}
                  onEdit={open}
                  onDelete={setDeleteTarget}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* =====================================================
          CREATE / EDIT MODAL
      ===================================================== */}

      <Modal
        open={modal}
        onClose={closeForm}
        title={editing ? "Edit banner" : "Create banner"}
        wide
      >
        <form onSubmit={save} className="space-y-5">
          {/* Form introduction */}
          <div className="rounded-xl bg-gradient-to-r from-brand-50 to-accent-50 p-4 sm:p-5">
            <div className="flex items-start gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-white text-brand-700 shadow-sm">
                <ImagePlus size={21} />
              </div>

              <div>
                <h3 className="font-semibold text-slate-900">
                  {editing
                    ? "Update website banner"
                    : "Add a new website banner"}
                </h3>

                <p className="mt-1 text-sm leading-6 text-slate-600">
                  Upload a high-quality image and configure
                  its visibility and display order.
                </p>
              </div>
            </div>
          </div>

          {/* Image section */}
          <section className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
            <SectionHeading
              icon={ImageIcon}
              title="Banner image"
              description="Choose the image visitors will see on your website."
            />

            <BannerImageUpload
              file={file}
              existingImage={editing?.image}
              onChange={setFile}
              required={!editing}
            />
          </section>

          {/* Basic information */}
          <section className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
            <SectionHeading
              icon={Monitor}
              title="Banner settings"
              description="Set the banner name, order, and publishing status."
            />

            <div className="space-y-4">
              <div>
                <label className={labelClass}>
                  Banner title
                  <span className="ml-1 text-red-500">*</span>
                </label>

                <input
                  className={inputClass}
                  value={form.title}
                  onChange={(event) =>
                    setForm((previous) => ({
                      ...previous,
                      title: event.target.value,
                    }))
                  }
                  placeholder="e.g. Homepage hero banner"
                  required
                  maxLength={150}
                />

                <p className="mt-1.5 text-xs text-slate-500">
                  {form.title.length}/150 characters
                </p>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className={labelClass}>
                    Display order
                  </label>

                  <div className="relative">
                    <ArrowUpDown
                      size={16}
                      className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500"
                    />

                    <input
                      className={`${inputClass} pl-10`}
                      type="number"
                      min="0"
                      step="1"
                      value={form.order}
                      onChange={(event) => {
                        const value = event.target.value;

                        setForm((previous) => ({
                          ...previous,
                          order:
                            value === ""
                              ? ""
                              : Math.max(0, Number(value)),
                        }));
                      }}
                      required
                    />
                  </div>

                  <p className="mt-1.5 text-xs leading-5 text-slate-500">
                    Lower numbers appear first when your website
                    sorts banners in ascending order.
                  </p>
                </div>

                <div>
                  <label className={labelClass}>
                    Publishing status
                  </label>

                  <label
                    className={`flex min-h-[46px] cursor-pointer items-center justify-between gap-3 rounded-lg border px-3.5 py-2.5 transition ${
                      form.isActive
                        ? "border-emerald-200 bg-emerald-50/60"
                        : "border-slate-200 bg-slate-50"
                    }`}
                  >
                    <div className="flex items-center gap-2.5">
                      {form.isActive ? (
                        <Eye
                          size={17}
                          className="text-emerald-600"
                        />
                      ) : (
                        <EyeOff
                          size={17}
                          className="text-slate-500"
                        />
                      )}

                      <div>
                        <p className="text-sm font-semibold text-slate-800">
                          {form.isActive ? "Active" : "Inactive"}
                        </p>

                        <p className="mt-0.5 text-xs text-slate-500">
                          {form.isActive
                            ? "Enabled for display"
                            : "Hidden from display"}
                        </p>
                      </div>
                    </div>

                    <input
                      type="checkbox"
                      className="h-4 w-4 shrink-0 accent-orange-500"
                      checked={Boolean(form.isActive)}
                      onChange={(event) =>
                        setForm((previous) => ({
                          ...previous,
                          isActive: event.target.checked,
                        }))
                      }
                    />
                  </label>
                </div>
              </div>
            </div>
          </section>

          {/* Live preview */}
          {(file || editing?.image) && (
            <section className="rounded-xl border border-slate-200 bg-slate-50/70 p-4">
              <div className="mb-3 flex items-center gap-2">
                <Eye size={17} className="text-brand-700" />

                <h3 className="text-sm font-semibold text-slate-800">
                  Banner summary
                </h3>
              </div>

              <div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-3">
                <div className="flex h-12 w-20 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-slate-100">
                  {file ? (
                    <FilePreview file={file} />
                  ) : (
                    <img
                      src={imageUrl(editing?.image)}
                      alt="Current banner"
                      className="h-full w-full object-cover"
                    />
                  )}
                </div>

                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-slate-900">
                    {form.title || "Untitled banner"}
                  </p>

                  <div className="mt-1.5 flex flex-wrap items-center gap-2">
                    <StatusBadge active={form.isActive} />

                    <span className="text-xs text-slate-500">
                      Order {form.order === "" ? 0 : form.order}
                    </span>
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* Form footer */}
          <div className="flex flex-col-reverse gap-2 border-t border-slate-100 pt-4 sm:flex-row sm:justify-end">
            <button
              type="button"
              className="btn-secondary justify-center"
              onClick={closeForm}
              disabled={saving}
            >
              Cancel
            </button>

            <button
              type="submit"
              className="btn-primary justify-center"
              disabled={saving}
            >
              {saving ? (
                <>
                  <RefreshCw
                    size={16}
                    className="animate-spin"
                  />
                  Saving banner...
                </>
              ) : (
                <>
                  <Check size={16} />
                  {editing ? "Save changes" : "Create banner"}
                </>
              )}
            </button>
          </div>
        </form>
      </Modal>

      {/* =====================================================
          VIEW BANNER MODAL
      ===================================================== */}

      <Modal
        open={viewModal}
        onClose={() => setViewModal(false)}
        title="Banner preview"
        wide
      >
        {viewBanner && (
          <div className="space-y-5">
            {/* Preview image */}
            <div className="overflow-hidden rounded-xl border border-slate-200 bg-slate-100">
              <div className="relative flex aspect-[16/8] items-center justify-center">
                {viewBanner.image ? (
                  <img
                    src={imageUrl(viewBanner.image)}
                    alt={viewBanner.title || "Banner"}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <ImageIcon
                    size={42}
                    className="text-slate-500"
                  />
                )}

                <div className="absolute left-3 top-3">
                  <StatusBadge
                    active={viewBanner.isActive !== false}
                  />
                </div>

                <div className="absolute right-3 top-3 rounded-lg bg-white/90 px-3 py-1.5 text-xs font-semibold text-slate-700 backdrop-blur">
                  Order {viewBanner.order ?? 0}
                </div>
              </div>
            </div>

            {/* Details */}
            <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
              <div className="flex items-start gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-700">
                  <ImageIcon size={21} />
                </div>

                <div className="min-w-0 flex-1">
                  <h3 className="break-words text-lg font-semibold text-slate-900">
                    {viewBanner.title || "Untitled banner"}
                  </h3>

                  <p className="mt-1 text-sm text-slate-500">
                    Website banner
                  </p>
                </div>
              </div>

              <div className="mt-5 grid gap-4 border-t border-slate-100 pt-4 sm:grid-cols-2">
                <div>
                  <p className="text-xs font-medium  text-slate-500">
                    Display order
                  </p>

                  <p className="mt-1.5 text-sm font-semibold text-slate-800">
                    {viewBanner.order ?? 0}
                  </p>
                </div>

                <div>
                  <p className="text-xs font-medium  text-slate-500">
                    Status
                  </p>

                  <div className="mt-1.5">
                    <StatusBadge
                      active={viewBanner.isActive !== false}
                    />
                  </div>
                </div>

                <div>
                  <p className="text-xs font-medium  text-slate-500">
                    Created
                  </p>

                  <p className="mt-1.5 flex items-center gap-2 text-sm text-slate-700">
                    <CalendarDays
                      size={15}
                      className="text-slate-500"
                    />

                    {formatDate(viewBanner.createdAt)}
                  </p>
                </div>

                <div>
                  <p className="text-xs font-medium  text-slate-500">
                    Last updated
                  </p>

                  <p className="mt-1.5 flex items-center gap-2 text-sm text-slate-700">
                    <CalendarDays
                      size={15}
                      className="text-slate-500"
                    />

                    {formatDate(viewBanner.updatedAt)}
                  </p>
                </div>
              </div>

              <div className="mt-4 border-t border-slate-100 pt-4">
                <p className="text-xs font-medium  text-slate-500">
                  Image URL
                </p>

                {viewBanner.image ? (
                  <a
                    href={imageUrl(viewBanner.image)}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-2 inline-flex max-w-full items-center gap-2 break-all text-sm font-medium text-brand-700 hover:text-brand-800"
                  >
                    <ExternalLink
                      size={15}
                      className="shrink-0"
                    />

                    {viewBanner.image}
                  </a>
                ) : (
                  <p className="mt-2 text-sm text-slate-500">
                    No image URL available.
                  </p>
                )}
              </div>

              {viewBanner._id && (
                <div className="mt-4 border-t border-slate-100 pt-4">
                  <p className="text-xs font-medium  text-slate-500">
                    Banner ID
                  </p>

                  <p className="mt-1.5 break-all font-mono text-xs text-slate-600">
                    {viewBanner._id}
                  </p>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex flex-col-reverse gap-2 border-t border-slate-100 pt-4 sm:flex-row sm:justify-end">
              <button
                type="button"
                className="btn-secondary justify-center"
                onClick={() => setViewModal(false)}
              >
                Close
              </button>

              <button
                type="button"
                className="btn-primary justify-center"
                onClick={() => {
                  const banner = viewBanner;

                  setViewModal(false);
                  open(banner);
                }}
              >
                <Pencil size={15} />
                Edit banner
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* =====================================================
          DELETE CONFIRMATION MODAL
      ===================================================== */}

      <Modal
        open={Boolean(deleteTarget)}
        onClose={() => {
          if (!deleting) setDeleteTarget(null);
        }}
        title="Delete banner"
      >
        <div className="space-y-5">
          <div className="flex items-start gap-3 rounded-lg border border-red-100 bg-red-50 p-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white text-red-600">
              <AlertCircle size={20} />
            </div>

            <div>
              <h3 className="font-semibold text-red-900">
                Are you sure?
              </h3>

              <p className="mt-1 text-sm leading-6 text-red-800">
                You are about to delete{" "}
                <strong>
                  {deleteTarget?.title || "this banner"}
                </strong>
                . This action cannot be undone.
              </p>
            </div>
          </div>

          {deleteTarget?.image && (
            <div className="flex items-center gap-3 rounded-lg border border-slate-200 p-3">
              <img
                src={imageUrl(deleteTarget.image)}
                alt={deleteTarget.title || "Banner"}
                className="h-14 w-24 shrink-0 rounded-lg bg-slate-100 object-cover"
              />

              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-slate-800">
                  {deleteTarget.title || "Untitled banner"}
                </p>

                <p className="mt-1 text-xs text-slate-500">
                  Display order: {deleteTarget.order ?? 0}
                </p>
              </div>
            </div>
          )}

          <div className="flex flex-col-reverse gap-2 border-t border-slate-100 pt-4 sm:flex-row sm:justify-end">
            <button
              type="button"
              className="btn-secondary justify-center"
              onClick={() => setDeleteTarget(null)}
              disabled={deleting}
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={remove}
              disabled={deleting}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {deleting ? (
                <>
                  <RefreshCw
                    size={16}
                    className="animate-spin"
                  />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 size={16} />
                  Delete banner
                </>
              )}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

/* =========================================================
   LOCAL FILE PREVIEW
========================================================= */

function FilePreview({ file }) {
  const [preview, setPreview] = useState("");

  useEffect(() => {
    if (!file) {
      setPreview("");
      return undefined;
    }

    const objectUrl = URL.createObjectURL(file);

    setPreview(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  if (!preview) {
    return <ImageIcon size={20} className="text-slate-500" />;
  }

  return (
    <img
      src={preview}
      alt="Selected banner"
      className="h-full w-full object-cover"
    />
  );
}
